import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Card,
    Space,
    Spin,
    App,
    Typography,
    Tag,
    Input,
    List,
    Row,
    Col,
    Divider,
    Empty
} from 'antd';
import {
    SearchOutlined,
    DollarOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    MinusCircleOutlined,
    StopOutlined
} from '@ant-design/icons';
import {devicePrintService, deviceSalesService} from '../services/posApi';
import { usePageTitle } from '../context/PageTitleContext';
import { useNavigate } from "react-router-dom";
import {IconDotFilled, IconDotPartially, IconDotUnfilled} from "../../../components/icons";
import Paragraph from "antd/es/typography/Paragraph";
import SaleReturnModal from "../components/SaleReturnModal";

const { Text, Title } = Typography;
const isElectron = typeof window !== 'undconst isElectron = typeof window !== \'undefined\' && window.process && window.process.type;\nefined' && window.process && window.process.type;


// Функция для форматирования даты в формате "1 июля в 23:11"
const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';

    const date = new Date(dateString);
    const months = [
        'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
        'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
    ];

    const day = date.getDate();
    const month = months[date.getMonth()];
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day} ${month} в ${hours}:${minutes}`;
};

// Функция для форматирования суммы
const formatAmount = (amount) => {
    if (!amount) return '0.00';
    return new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
};

// Функция для вызова экранной клавиатуры Windows
const showVirtualKeyboard = () => {
    if (isElectron && window.require) {
        const { shell } = window.require('electron');
        shell.openExternal('ms-appx-web://microsoft.windows.inputapp/');
    }
};

// Функция для получения правильного окончания слова "позиция"
const getItemsWord = (count) => {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
        return 'позиций';
    }

    if (lastDigit === 1) {
        return 'позиция';
    }

    if (lastDigit >= 2 && lastDigit <= 4) {
        return 'позиции';
    }

    return 'позиций';
};

// Иконки для статусов
const StatusIcons = {
    IconDotFilled: IconDotFilled,
    IconDotPartially: IconDotPartially,
    IconDotUnfilled: IconDotUnfilled,
};

// Функция для рендеринга статуса оплаты
const renderPaymentStatus = (status) => {
    if (!status) return null;

    const statusConfig = {
        'paid': { color: 'default', text: 'Оплачено', icon: StatusIcons.IconDotFilled },
        'partially_refunded': { color: 'purple', text: 'Частично возвращено', icon: StatusIcons.IconDotPartially },
        'refunded': { color: 'default', text: 'Возвращено', icon: StatusIcons.IconDotFilled },
        'pending': { color: 'orange', text: 'Ожидается оплата', icon: StatusIcons.IconDotUnfilled }
    };

    const config = statusConfig[status];
    if (!config) return null;

    const IconComponent = config.icon;

    return (
        <Tag color={config.color} icon={<IconComponent />}>
            {config.text}
        </Tag>
    );
};

// Функция для рендеринга статуса выполнения
const renderFulfillmentStatus = (status) => {
    if (!status) return null;

    const statusConfig = {
        'fulfilled': { color: 'default', text: 'Собрано', icon: StatusIcons.IconDotFilled },
        'unfulfilled': { color: 'gold', text: 'Не собрано', icon: StatusIcons.IconDotFilled },
        'partially_fulfilled': { color: 'orange', text: 'Частично собрано', icon: StatusIcons.IconDotPartially },
        'scheduled': { color: 'purple', text: 'Ожидает сборки', icon: StatusIcons.IconDotUnfilled }
    };

    const config = statusConfig[status];
    if (!config) return null;

    const IconComponent = config.icon;

    return (
        <Tag color={config.color} icon={<IconComponent />}>
            {config.text}
        </Tag>
    );
};

// Функция для рендеринга статуса возврата
const renderReturnStatus = (status) => {
    if (!status) return null;

    const statusConfig = {
        'is_progress': { color: 'blue', text: 'Возврат в процессе', icon: StatusIcons.IconDotUnfilled },
        'closed': { color: 'default', text: 'Возврат завершён', icon: StatusIcons.IconDotFilled }
    };

    const config = statusConfig[status];
    if (!config) return null;

    const IconComponent = config.icon;

    return (
        <Tag color={config.color} icon={<IconComponent />}>
            {config.text}
        </Tag>
    );
};

// Функция для рендеринга статуса чека
const renderReceiptStatus = (status) => {
    const statusConfig = {
        'completed': 'Подтверждён',
        'pending': 'Ожидает подтверждения',
        'failed': 'Ошибка при отправке',
        'no_data': 'Пробит, нет данных'
    };

    return statusConfig[status] || status;
};

// Функция для дебаунсинга
const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

const DeviceSalesList = () => {
    const { setPageTitle, setRightContent } = usePageTitle();
    const navigate = useNavigate();
    const { message } = App.useApp();

    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [nextCursor, setNextCursor] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState(false);
    const [selectedSale, setSelectedSale] = useState(null);
    const [saleDetails, setSaleDetails] = useState(null);
    const [saleDetailsLoading, setSaleDetailsLoading] = useState(false);

    const [printingReceipt, setPrintingReceipt] = useState(null);

    const [returnModalVisible, setReturnModalVisible] = useState(false);
    const [returnSaleId, setReturnSaleId] = useState(null);

    const observer = useRef();
    const lastSaleElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                loadMoreSales();
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore, error]);

    // Устанавливаем заголовок страницы
    useEffect(() => {
        setPageTitle('Продажи');

        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle, setRightContent, navigate]);

    // Загрузка записей при первой загрузке и при изменении поиска
    useEffect(() => {
        setError(false);
        fetchSales();
    }, [searchText]);

    // Функция для дебаунсинга поиска
    const debouncedSearch = useCallback(
        debounce((value) => {
            setSearchText(value);
        }, 500),
        []
    );

    // Обработчик изменения поля поиска
    const handleSearch = (e) => {
        debouncedSearch(e.target.value);
    };

    // Загрузка записей
    const fetchSales = async () => {
        try {
            setLoading(true);
            setSales([]);
            setNextCursor(null);
            setHasMore(true);
            setSelectedSale(null);
            setSaleDetails(null);

            const response = await deviceSalesService.getSales({
                search: searchText
            });

            if (response && response.status === 'success') {
                setSales(response.data.items);
                setNextCursor(response.data.pagination.next_cursor);
                setHasMore(response.data.pagination.has_more);
                setTotal(response.data.pagination.total);
            } else {
                message.error(response?.message || 'Не удалось загрузить список продаж');
            }
        } catch (error) {
            setError(true);
            message.error(error.message || 'Ошибка при загрузке продаж');
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    };

    // Загрузка дополнительных продаж при прокрутке
    const loadMoreSales = async () => {
        if (!hasMore || loading || error) return;

        try {
            setLoading(true);

            const response = await deviceSalesService.getSales({
                cursor: nextCursor,
                search: searchText
            });

            if (response && response.status === 'success') {
                setSales(prev => [...prev, ...response.data.items]);
                setNextCursor(response.data.pagination.next_cursor);
                setHasMore(response.data.pagination.has_more);
                setTotal(response.data.pagination.total);
            } else {
                message.error(response?.message || 'Не удалось загрузить больше продаж');
            }
        } catch (error) {
            setError(true);
            message.error(error.message || 'Ошибка при загрузке дополнительных продаж');
        } finally {
            setLoading(false);
        }
    };

    // Загрузка деталей продажи
    const fetchSaleDetails = async (id) => {
        try {
            setSaleDetailsLoading(true);
            const response = await deviceSalesService.getSaleById(id);

            if (response && response.result === 'success') {
                setSaleDetails(response.data);
            } else {
                message.error('Не удалось загрузить детали продажи');
            }
        } catch (error) {
            message.error(error.message || 'Ошибка при загрузке деталей продажи');
        } finally {
            setSaleDetailsLoading(false);
        }
    };

    // Обработчик клика на продажу
    const handleSaleClick = (sale) => {
        setSelectedSale(sale.id);
        fetchSaleDetails(sale.id);
    };

    // Функция для получения чека
    const fetchReceipt = async (receiptId, receiptStatus) => {
        try {
            setPrintingReceipt(receiptId);

            if (receiptStatus !== 'completed') {
                message.warning('Чек можно распечатать только после подтверждения');
                return;
            }

            const response = await deviceSalesService.getReceipt(receiptId);

            if (response && response.result === 'success') {
                await printReceiptUniversal(response.receipt);
            } else {
                message.error(response?.message || 'Не удалось получить чек');
            }
        } catch (error) {
            message.error(error.message || 'Ошибка при получении чека');
        } finally {
            setPrintingReceipt(null);
        }
    };

    // Универсальная функция печати чека
    const printReceiptUniversal = useCallback(async (receiptHtml) => {
        try {
            if (isElectron) {
                await devicePrintService.printReceipt(receiptHtml);
            } else {
                const printWindow = window.open('', '_blank');
                if (printWindow) {
                    printWindow.document.write(receiptHtml);
                    printWindow.document.close();
                    printWindow.focus();
                    printWindow.print();
                    printWindow.close();
                } else {
                    throw new Error('Не удалось открыть окно печати');
                }
            }
        } catch (error) {
            console.error('Ошибка при печати чека:', error);
            throw error;
        }
    }, []);

    // Функция для открытия модала возврата
    const handleReturnClick = (saleId) => {
        setReturnSaleId(saleId);
        setReturnModalVisible(true);
    };

    // Функция для успешного создания возврата
    const handleReturnSuccess = () => {
        fetchSales(); // Обновляем список продаж
        if (selectedSale) {
            fetchSaleDetails(selectedSale); // Обновляем детали продажи
        }
    };

    // Рендер элемента списка продаж
    const renderSaleItem = (sale) => (
        <List.Item
            onClick={() => handleSaleClick(sale)}
            style={{
                cursor: 'pointer',
                backgroundColor: selectedSale === sale.id ? 'var(--ant-color-fill-tertiary)' : 'transparent',
                borderRadius: 4,
                paddingRight: 16,
                paddingBottom: 12,
                paddingLeft: 16,
                paddingTop: 12,
                marginBottom: 0
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <div style={{ flex: 1 }}>
                    <div>
                        <Text>#{sale.id}</Text>
                    </div>
                    <div style={{ marginBottom: 4 }}>
                        <Text type="secondary">{formatDate(sale.sale_date)}</Text>
                    </div>
                    <div>
                        <Space wrap>
                            {renderPaymentStatus(sale.payment_status)}
                            {renderFulfillmentStatus(sale.fulfillment_status)}
                            {renderReturnStatus(sale.return_status)}
                        </Space>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Text>
                        {formatAmount(sale.total_amount)} ₸
                    </Text>
                </div>
            </div>
        </List.Item>
    );

    // Рендер деталей продажи
    const renderSaleDetails = () => {
        if (!saleDetails) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Empty description="Выберите продажу для просмотра деталей" />
                </div>
            );
        }

        if (saleDetailsLoading) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Spin size="large" />
                </div>
            );
        }

        const { sale, payments, receipts, refunds, cashier, customer } = saleDetails;

        return (
            <Row gutter={16}>
                <Col span={18}>
                    <div style={{
                        height: 'calc(100vh - 69px)',
                        overflowY: 'auto',
                        paddingRight: 16
                    }}>
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            {/* Заголовок с основной информацией */}
                            <Card style={{marginTop: 24}}>
                                <Title level={3} style={{marginTop: 0}}>#{sale.id}</Title>
                                <Paragraph>{formatDate(sale.sale_date)}, {cashier.name}</Paragraph>
                                <Space wrap>
                                    {renderPaymentStatus(payments.payment_status)}
                                    {renderFulfillmentStatus(sale.fulfillment_status)}
                                    {renderReturnStatus(sale.return_status)}
                                </Space>
                            </Card>

                            {/* Блок возвратов */}
                            {refunds && refunds.length > 0 && (
                                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                    {refunds.map((refund, index) => (
                                        <Card key={index} title={<Space>{renderReturnStatus(refund.return_status)}<Text type="secondary">#{refund.id}, {formatDate(refund.sale_date)}</Text></Space>}>
                                            <List
                                                size="small"
                                                dataSource={refund.items}
                                                renderItem={item => (
                                                    <List.Item style={{padding: '16px 0'}}>
                                                        <div style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: 'minmax(6.25rem, 6fr) minmax(5.625rem, auto) minmax(5rem, auto)',
                                                            gridTemplateAreas: '"col-2 col-3 col-4"',
                                                            gap: '16px',
                                                            width: '100%',
                                                            alignItems: 'start'
                                                        }}>
                                                            <div style={{
                                                                gridArea: 'col-2',
                                                                wordWrap: 'break-word',
                                                                wordBreak: 'break-word',
                                                                lineHeight: '1.4',
                                                                fontSize: '14px'
                                                            }}>
                                                                {item.name}
                                                            </div>
                                                            <div style={{
                                                                gridArea: 'col-3',
                                                                textAlign: 'right',
                                                                fontWeight: '500',
                                                                fontSize: '14px',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {formatAmount(item.unit_price)} ₸&nbsp;&nbsp;×&nbsp;&nbsp;{item.quantity}
                                                            </div>
                                                            <div style={{
                                                                gridArea: 'col-4',
                                                                textAlign: 'right',
                                                                fontWeight: '500',
                                                                fontSize: '14px',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {formatAmount(item.total_amount)} ₸
                                                            </div>
                                                        </div>
                                                    </List.Item>
                                                )}
                                            />
                                        </Card>
                                    ))}
                                </Space>
                            )}

                        {/* Блок несобранных товаров */}
                        {sale.unfulfulled_items && sale.unfulfulled_items.length > 0 && (
                            <Card title={<Space><Tag color="gold">Не собрано</Tag></Space>}>
                                <List
                                    size="small"
                                    dataSource={sale.unfulfulled_items}
                                    renderItem={item => (
                                        <List.Item style={{padding: '16px 0'}}>
                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'minmax(6.25rem, 6fr) minmax(5.625rem, auto) minmax(5rem, auto)',
                                                gridTemplateAreas: '"col-2 col-3 col-4"',
                                                gap: '16px',
                                                width: '100%',
                                                alignItems: 'start'
                                            }}>
                                                <div style={{
                                                    gridArea: 'col-2',
                                                    wordWrap: 'break-word',
                                                    wordBreak: 'break-word',
                                                    lineHeight: '1.4',
                                                    fontSize: '14px'
                                                }}>
                                                    {item.name}
                                                </div>
                                                <div style={{
                                                    gridArea: 'col-3',
                                                    textAlign: 'right',
                                                    fontWeight: '500',
                                                    fontSize: '14px',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {formatAmount(item.unit_price)} ₸&nbsp;&nbsp;×&nbsp;&nbsp;{item.quantity}
                                                </div>
                                                <div style={{
                                                    gridArea: 'col-4',
                                                    textAlign: 'right',
                                                    fontWeight: '500',
                                                    fontSize: '14px',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {formatAmount(item.total_amount)} ₸
                                                </div>
                                            </div>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        )}

                        {/* Блок собранных товаров */}
                        {sale.items && sale.items.length > 0 && (
                            <Card title={<Space><Tag color="default">Собрано</Tag></Space>}>
                                <List
                                    size="small"
                                    dataSource={sale.items}
                                    renderItem={item => (
                                        <List.Item style={{padding: '16px 0'}}>
                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'minmax(6.25rem, 6fr) minmax(5.625rem, auto) minmax(5rem, auto)',
                                                gridTemplateAreas: '"col-2 col-3 col-4"',
                                                gap: '16px',
                                                width: '100%',
                                                alignItems: 'start'
                                            }}>
                                                <div style={{
                                                    gridArea: 'col-2',
                                                    wordWrap: 'break-word',
                                                    wordBreak: 'break-word',
                                                    lineHeight: '1.4',
                                                    fontSize: '14px'
                                                }}>
                                                    {item.name}
                                                </div>
                                                <div style={{
                                                    gridArea: 'col-3',
                                                    textAlign: 'right',
                                                    fontWeight: '500',
                                                    fontSize: '14px',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {formatAmount(item.unit_price)} ₸&nbsp;&nbsp;×&nbsp;&nbsp;{item.quantity}
                                                </div>
                                                <div style={{
                                                    gridArea: 'col-4',
                                                    textAlign: 'right',
                                                    fontWeight: '500',
                                                    fontSize: '14px',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {formatAmount(item.total_amount)} ₸
                                                </div>
                                            </div>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        )}

                        {/* Блок оплат */}
                        <Card title={<Space><Tag color="default">{payments.payment_status === 'paid' ? 'Оплачено' : 'Не оплачено'}</Tag></Space>}>
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Row justify="space-between" align="middle">
                                    <Col span={6}>
                                        <Text strong>Итого</Text>
                                    </Col>
                                    <Col flex="auto">
                                        <Text strong>{payments.total.qty_items} {getItemsWord(payments.total.qty_items)}</Text>
                                    </Col>
                                    <Col>
                                        <Text strong>{formatAmount(payments.total.amount)} ₸</Text>
                                    </Col>
                                </Row>

                                {payments.tax && payments.tax.rate && payments.tax.rate > 0 && (
                                    <Row justify="space-between" align="middle">
                                        <Col span={6}>
                                            <Text>Налог</Text>
                                        </Col>
                                        <Col flex="auto">
                                            <Text>в т.ч. НДС {payments.tax.rate}%</Text>
                                        </Col>
                                        <Col>
                                            <Text>{formatAmount(payments.tax.amount)} ₸</Text>
                                        </Col>
                                    </Row>
                                )}

                                {customer.transactions && customer.transactions.map((transaction, index) => (
                                    <Row key={index} justify="space-between" align="middle">
                                        <Col span={6}>
                                            <Text>{index === 0 ? 'Бонусы клиенту' : ''}</Text>
                                        </Col>
                                        <Col flex="auto">
                                            <Text>Транзакция #{transaction.id}</Text>
                                        </Col>
                                        <Col>
                                            <Text>{formatAmount(transaction.amount)} Б</Text>
                                        </Col>
                                    </Row>
                                ))}

                                <Divider style={{ margin: '8px 0' }} />

                                {payments.paid && payments.paid.map((payment, index) => (
                                    <Row key={index} justify="space-between" align="middle">
                                        <Col span={6}>
                                            <Text>{index === 0 ? 'Оплачено' : ''}</Text>
                                        </Col>
                                        <Col flex="auto">
                                            <Text>{payment.payment_method_name}</Text>
                                        </Col>
                                        <Col>
                                            <Text>{formatAmount(payment.amount)} ₸</Text>
                                        </Col>
                                    </Row>
                                ))}

                                {payments.refunded && payments.refunded.map((refund, index) => (
                                    <Row key={index} justify="space-between" align="middle">
                                        <Col span={6}>
                                            <Text>{index === 0 ? 'Возврат' : ''}</Text>
                                        </Col>
                                        <Col flex="auto">
                                            <Text>{refund.payment_method_name}</Text>
                                        </Col>
                                        <Col>
                                            <Text>{formatAmount(refund.amount)} ₸</Text>
                                        </Col>
                                    </Row>
                                ))}

                                {payments.refunded && payments.refunded.length > 0 && (
                                    <Row justify="space-between" align="middle" style={{ marginTop: 8 }}>
                                        <Col span={6}>
                                            <Text strong>Чистая сумма оплаты</Text>
                                        </Col>
                                        <Col flex="auto" />
                                        <Col>
                                            <Text strong>{formatAmount(payments.net_payment)} ₸</Text>
                                        </Col>
                                    </Row>
                                )}
                            </Space>
                        </Card>
                    </Space>
                    </div>
                </Col>
                <Col span={6}>
                    <Space direction="vertical" style={{ width: '100%', marginTop: 24, paddingRight: 24}}>
                        {receipts.map((receipt) => (
                            <Card
                                key={receipt.id}
                                size="medium"
                                style={{ cursor: 'pointer' }}
                                onClick={() => fetchReceipt(receipt.id, receipt.status)}
                                loading={printingReceipt === receipt.id}
                            >
                                <Space direction="vertical" size="small">
                                    <Text strong>
                                        Чек {receipt.type === 'sale' ? 'продажи' : 'возврата'} #{receipt.ticket_number}
                                    </Text>
                                    <Text type="secondary">
                                        {renderReceiptStatus(receipt.status)}
                                    </Text>
                                </Space>
                            </Card>
                        ))}

                        <Card
                            size="medium"
                            onClick={() => handleReturnClick(sale.id)}
                            style={{ cursor: 'pointer' }}
                        >
                            <Text strong>Возврат</Text>
                        </Card>
                    </Space>
                </Col>
            </Row>
        );
    };

    return (
        <div style={{
            padding: '0 0px',
            height: 'calc(100vh - 45px)',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            overflow: 'hidden'
        }}>
            <Row gutter={16}>
                <Col span={8}>
                    <Card>
                        <div style={{ marginBottom: 16 }}>
                            <Input
                                placeholder="Поиск продаж..."
                                prefix={<SearchOutlined />}
                                onChange={handleSearch}
                                onFocus={showVirtualKeyboard}
                                allowClear
                            />
                        </div>

                        {initialLoading ? (
                            <div style={{ textAlign: 'center', padding: '50px 0' }}>
                                <Spin size="large" />
                            </div>
                        ) : (
                            <div style={{ height: 'calc(100vh - 149px)', overflowY: 'auto' }}>
                                <List
                                    dataSource={sales}
                                    renderItem={renderSaleItem}
                                    locale={{ emptyText: 'Нет данных' }}
                                />
                                {loading && (
                                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                                        <Spin />
                                    </div>
                                )}
                                {/* Элемент для отслеживания прокрутки */}
                                {hasMore && (
                                    <div
                                        ref={lastSaleElementRef}
                                        style={{ height: 10, width: '100%' }}
                                    />
                                )}
                                {!hasMore && sales.length > 0 && (
                                    <div style={{ textAlign: 'center', padding: '10px' }}>
                                        <Text type="secondary">
                                            Больше записей нет
                                        </Text>
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>
                </Col>
                <Col span={16}>
                        {renderSaleDetails()}
                </Col>
            </Row>
            <SaleReturnModal
                visible={returnModalVisible}
                onClose={() => setReturnModalVisible(false)}
                saleId={returnSaleId}
                onSuccess={handleReturnSuccess}
            />
        </div>
    );
};

export default DeviceSalesList;